import { useCallback, useRef, useState } from 'react';

const DEFAULT_LOOKAHEAD = 2;

/**
 * Keeps the detailed order list a couple of pages ahead of the reader.
 *
 * A page turn costs a round trip the user waits on, so the pages just past the
 * one on screen are fetched in the background and kept in memory. Landing on a
 * cached page renders immediately and tops the cache back up, which makes the
 * lookahead follow whichever direction the reader is moving.
 *
 * @param fetchPage   (page, pageSize) => Promise<detailedReport>
 * @param onPageReady (detailedReport) => void, called with the page to render
 * @param onError     (error) => void, only for pages the reader asked for
 * @param lookahead   how many pages past the current one to keep warm
 */
export default function useDetailedOrdersPager({
  fetchPage,
  onPageReady,
  onError,
  lookahead = DEFAULT_LOOKAHEAD,
}) {
  const [isPageLoading, setIsPageLoading] = useState(false);

  const cacheRef = useRef(new Map());
  const inFlightRef = useRef(new Map());
  // Bumped whenever the cached pages stop describing the report on screen. Any
  // response tagged with an older generation is dropped instead of rendered.
  const generationRef = useRef(0);
  // The page the reader actually wants. A response that arrives after they have
  // moved on belongs to a page nobody is looking at.
  const requestedPageRef = useRef(0);
  const pageSizeRef = useRef(null);
  const totalPagesRef = useRef(null);

  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const onPageReadyRef = useRef(onPageReady);
  onPageReadyRef.current = onPageReady;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const reset = useCallback(() => {
    generationRef.current += 1;
    cacheRef.current = new Map();
    inFlightRef.current = new Map();
    requestedPageRef.current = 0;
    pageSizeRef.current = null;
    totalPagesRef.current = null;
    setIsPageLoading(false);
  }, []);

  /** Returns the in-flight promise for a page, starting one if needed. */
  const load = useCallback((page, pageSize) => {
    const existing = inFlightRef.current.get(page);
    if (existing) {
      return existing;
    }

    const generation = generationRef.current;
    const promise = Promise.resolve(fetchPageRef.current(page, pageSize))
      .then((detailedReport) => {
        if (generation !== generationRef.current) {
          return null;
        }
        if (detailedReport) {
          cacheRef.current.set(page, detailedReport);
          const totalPages = detailedReport.pagination?.orders?.totalPages;
          if (typeof totalPages === 'number') {
            totalPagesRef.current = totalPages;
          }
        }
        return detailedReport;
      })
      .finally(() => {
        if (generation === generationRef.current) {
          inFlightRef.current.delete(page);
        }
      });

    inFlightRef.current.set(page, promise);
    return promise;
  }, []);

  /**
   * Warms the pages after `page`. Failures here are deliberately silent: nobody
   * asked for these pages, and navigating to one runs the foreground path,
   * which reports its own errors.
   */
  const prefetch = useCallback(
    (page, pageSize) => {
      for (let offset = 1; offset <= lookahead; offset += 1) {
        const target = page + offset;
        if (totalPagesRef.current !== null && target >= totalPagesRef.current) {
          break;
        }
        if (cacheRef.current.has(target) || inFlightRef.current.has(target)) {
          continue;
        }
        load(target, pageSize).catch(() => {});
      }
    },
    [lookahead, load],
  );

  /**
   * Seeds the cache with a page that arrived some other way, typically page 0
   * riding along with the report itself, and starts the lookahead from it.
   */
  const primePage = useCallback(
    (page, pageSize, detailedReport) => {
      if (!detailedReport?.pagination?.orders) {
        return;
      }
      pageSizeRef.current = pageSize;
      requestedPageRef.current = page;
      cacheRef.current.set(page, detailedReport);
      totalPagesRef.current =
        detailedReport.pagination.orders.totalPages ?? null;
      prefetch(page, pageSize);
    },
    [prefetch],
  );

  const goToPage = useCallback(
    async (page, pageSize) => {
      // A different page size means the cached cuts no longer line up.
      if (pageSizeRef.current !== null && pageSizeRef.current !== pageSize) {
        reset();
      }
      pageSizeRef.current = pageSize;
      requestedPageRef.current = page;

      const cached = cacheRef.current.get(page);
      if (cached) {
        onPageReadyRef.current(cached);
        prefetch(page, pageSize);
        return;
      }

      const generation = generationRef.current;
      setIsPageLoading(true);
      try {
        const detailedReport = await load(page, pageSize);
        if (
          generation !== generationRef.current ||
          requestedPageRef.current !== page ||
          !detailedReport
        ) {
          return;
        }
        onPageReadyRef.current(detailedReport);
        prefetch(page, pageSize);
      } catch (error) {
        // This page is the one on screen, so the reader needs to be told.
        if (generation === generationRef.current) {
          onErrorRef.current(error);
        }
      } finally {
        if (
          generation === generationRef.current &&
          requestedPageRef.current === page
        ) {
          setIsPageLoading(false);
        }
      }
    },
    [load, prefetch, reset],
  );

  return { primePage, goToPage, reset, isPageLoading };
}
